import Request from '@/services/util/request-service';
import toast from 'react-hot-toast';
import UtilityService from '../util/utility-service';
import ActivityUtilityService from './lesson/activity/activity-utility-service';
import ProgressUtilityService from './progress-utility-service';
import { BlockingContent, CourseId, SubunitId, UnitId } from 'course-types';

export interface AssignmentData {
  id: string;
  name: string;
  due_date?: Date | null;
  index: number;
  parent_subunit?: SubunitId;
  parent_unit?: UnitId;
  description?: string;
  submission?: SubmissionData;
  s3_url?: boolean;
}

export interface SubmissionData {
  submissionDate?: Date;
  grade?: number;
}

export interface AssignmentResponse {
  assignment?: AssignmentData;
  submission?: SubmissionData;
  blob?: Blob;
  mimeType?: string;
  isLocked?: boolean;
  error?: string;
  blockingContent?: BlockingContent;
}

export interface SubmitAssignmentResponse {
  message?: string;
  submission?: {
    submissionDate?: string;
  };
  error?: string;
}

const AssignmentService = {
  /**
   * Get all assignments from course data
   */
  getAllAssignments(courseData: any): Array<{
    unitId: string;
    subunitId: string | null;
    subunitData: any | null;
    data: any;
  }> {
    const assignmentData = [];
    for (const u in courseData.units) {
      const unitData = courseData.units[u];
      if (unitData.activities) {
        for (const a of unitData.activities) {
          if (typeof a !== 'number' && a.type === 'assignment') {
            assignmentData.push({ unitId: u, subunitId: null, subunitData: null, data: a });
          }
        }
      }
      for (const s in unitData.subunits) {
        const subData = unitData.subunits[s];
        for (const a of subData.activities) {
          if (typeof a !== 'number') {
            if (a.type === 'assignment') {
              assignmentData.push({
                unitId: u,
                subunitId: s,
                subunitData: subData,
                data: a,
              });
            }
          }
        }
      }
    }
    return assignmentData;
  },

  /**
   * Fetch all assignments and submissions for a group
   */
  async getGroupAssignments(
    groupId: string,
    courseId: string,
  ): Promise<
    Array<{
      assignment_id: string;
      assignment_name: string;
      grades: Record<string, number | null | undefined>;
    }>
  > {
    try {
      const response = await Request.post('progress/group/assignment/all', {
        groupId,
        courseId,
      });

      if (!response) {
        throw new Error('Failed to fetch group assignments');
      }

      const data = await response.json();
      return data?.groupData || [];
    } catch (error) {
      console.error('Error fetching group assignments:', error);
      toast.error('Failed to load group assignments');
      throw error;
    }
  },

  /**
   * Fetch assignment data, submission status, and file blob
   * Server returns JSON with assignment metadata and optionally blob/mimeType
   */
  async getAssignment(assignmentId: string): Promise<AssignmentResponse | null> {
    try {
      const response = await Request.get(`assignment/${assignmentId}`);

      if (!response) {
        toast.error('Assignment not found');
        return null;
      }

      const data: AssignmentResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching assignment:', error);
      toast.error('Failed to load assignment');
      return null;
    }
  },

  /**
   * Initialize assignment open date and update progress
   */
  async initializeAssignmentOpen(
    assignmentId: string,
    courseId: CourseId,
    unitId?: UnitId,
    subunitId?: SubunitId,
  ): Promise<void> {
    const { progressCookie, activityData } = ActivityUtilityService.retrieveActivityProgressLocal(
      courseId,
      unitId,
      subunitId,
    );

    activityData.assignment ??= {};

    if (!activityData?.assignment?.[assignmentId]) {
      activityData.assignment[assignmentId] = {};
    }

    if (
      activityData.assignment[assignmentId].openDate === undefined ||
      activityData.assignment[assignmentId].openDate === null
    ) {
      activityData.assignment[assignmentId].openDate = new Date();

      ActivityUtilityService.updateActivityProgressLocal(
        progressCookie,
        activityData,
        courseId,
        unitId,
        subunitId,
      );
      await ProgressUtilityService.updateUserCourseProgressDatabase(
        courseId,
        activityData,
        unitId,
        subunitId,
      );
    }
  },

  /**
   * Fetch assignment file blob for viewing
   */
  async fetchAssignmentFile(
    assignmentId: string,
  ): Promise<{ blob: Blob; mimeType: string } | null> {
    try {
      const response = await Request.get(`content/assignment/${assignmentId}`);

      if (!response) {
        throw new Error('Failed to fetch assignment file');
      }

      const contentType = response.headers.get('Content-Type') ?? '';

      if (contentType.includes('application/')) {
        const blob = await response.blob();
        return { blob, mimeType: contentType };
      }

      return null;
    } catch (error) {
      console.error('Error fetching assignment file:', error);
      toast.error('Failed to load assignment file');
      return null;
    }
  },

  /**
   * Fetch submission file blob for viewing/downloading
   */
  async fetchSubmissionFile(
    assignmentId: string,
  ): Promise<{ blob: Blob; mimeType: string } | null> {
    try {
      const response = await Request.get(`content/assignment/${assignmentId}/submission`);

      if (!response) {
        throw new Error('Failed to fetch submission file');
      }

      const contentType = response.headers.get('Content-Type') ?? '';

      if (contentType.includes('application/')) {
        const blob = await response.blob();
        return { blob, mimeType: contentType };
      }

      return null;
    } catch (error) {
      console.error('Error fetching submission file:', error);
      toast.error('Failed to load submission file');
      return null;
    }
  },

  /**
   * Submit assignment file
   */
  async submitAssignment(
    assignmentId: string,
    file: File,
    courseId: CourseId,
    unitId?: UnitId,
    subunitId?: SubunitId,
  ): Promise<SubmitAssignmentResponse> {
    try {
      const { progressCookie, activityData } = ActivityUtilityService.retrieveActivityProgressLocal(
        courseId,
        unitId,
        subunitId,
      );
      const formBody = new FormData();
      formBody.append('courseId', courseId);
      formBody.append('assignmentId', assignmentId);
      formBody.append('name', file.name);

      if (unitId) formBody.append('unitId', unitId);
      if (subunitId) formBody.append('subunitId', subunitId);

      formBody.append('file', file);

      const response = await Request.post('assignment/submit', formBody);

      if (!response) {
        throw new Error('Failed to submit assignment');
      }

      // Server returns JSON response with submission data
      const result: SubmitAssignmentResponse = await response.json();

      if (result.error) {
        throw new Error(result.error);
      }

      toast.success('Assignment submitted successfully!');

      // Update local activity progress
      if (activityData && progressCookie && result.submission) {
        activityData.assignment ??= {};
        activityData.assignment[assignmentId] = {
          openDate: activityData.assignment[assignmentId]?.openDate,
          submissionDate: result.submission.submissionDate
            ? new Date(result.submission.submissionDate)
            : new Date(),
        };

        ActivityUtilityService.updateActivityProgressLocal(
          progressCookie,
          activityData,
          courseId,
          unitId,
          subunitId,
        );
      }

      return result;
    } catch (error) {
      console.error('Error submitting assignment:', error);
      toast.error('Failed to submit assignment');
      return { error: 'Failed to submit assignment' };
    }
  },

  /**
   * Grade a student submission
   */
  async gradeSubmission(
    assignmentId: string,
    email: string,
    grade: number,
  ): Promise<{ success: boolean; message?: string; graded_submission?: { grade: number } }> {
    try {
      const response = await Request.post('assignment/grade', {
        assignmentId,
        email,
        grade,
      });

      if (!response) {
        throw new Error('Failed to grade submission');
      }

      const result = await response.json();

      // Check for error response from API
      if (result.error) {
        return { success: false, message: result.error };
      }

      return {
        success: true,
        message: result.message,
        graded_submission: result.graded_submission,
      };
    } catch (error) {
      console.error('Error grading submission:', error);
      return { success: false, message: 'Error while grading assignment.' };
    }
  },

  /**
   * Download submission file
   */
  async downloadSubmission(assignmentId: string, assignmentName: string): Promise<void> {
    if (!assignmentId) {
      toast.error('No submission file available for download');
      return;
    }

    try {
      const result = await this.fetchSubmissionFile(assignmentId);

      if (result) {
        UtilityService.downloadBlob(result.blob, assignmentName || 'submission');
      } else {
        toast.error('Failed to download submission');
      }
    } catch (error) {
      console.error('Error downloading submission:', error);
      toast.error('Failed to download submission');
    }
  },

  /**
   * Delete submission with validation and user confirmation
   */
  async deleteSubmission(
    assignment: AssignmentData,
    courseId: CourseId,
    email?: string,
  ): Promise<{ success: boolean }> {
    const { progressCookie, activityData } = ActivityUtilityService.retrieveActivityProgressLocal(
      courseId,
      assignment.parent_unit,
      assignment.parent_subunit,
    );
    // Validate deletion is allowed
    if (assignment?.due_date && new Date() > new Date(assignment.due_date)) {
      toast.error('Cannot delete submission after due date');
      throw new Error('Cannot delete submission after due date');
    }

    if (assignment.submission?.grade !== undefined && assignment.submission?.grade !== null) {
      toast.error('Cannot delete graded submission');
      throw new Error('Cannot delete graded submission');
    }

    try {
      const response = await Request.delete('assignment/submission', {
        assignmentId: assignment?.id,
        courseId,
        email,
      });

      if (response && response.status === 200) {
        toast.success('Submission deleted successfully');

        // Remove submission date from local activity progress
        if (activityData && progressCookie && assignment?.id) {
          activityData.assignment[assignment.id] = {
            ...activityData.assignment[assignment.id],
            submissionDate: undefined,
          };

          ActivityUtilityService.updateActivityProgressLocal(
            progressCookie,
            activityData,
            courseId,
            assignment.parent_unit,
            assignment.parent_subunit,
          );
        }

        return { success: true };
      } else {
        toast.error('Failed to delete submission');
        return { success: false };
      }
    } catch (error) {
      console.error('Error deleting submission:', error);
      toast.error('Failed to delete submission');
      return { success: false };
    }
  },
};

export default AssignmentService;
